import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { MainLayout } from '@/layouts/MainLayout';
import { Login } from '@/features/auth/Login';
import { Register } from '@/features/auth/Register';
import { ForgotPassword } from '@/features/auth/ForgotPassword';
import { ResetPassword } from '@/features/auth/ResetPassword';
import { StravaCallback } from '@/features/auth/StravaCallback';
import { GoogleCallback } from '@/features/auth/GoogleCallback';
import { CompleteProfile } from '@/features/auth/CompleteProfile';
import { HomePage } from '@/pages/HomePage';
import { RacesPage } from '@/pages/RacesPage';
import { RankingPage } from '@/pages/RankingPage';
import { CreateRacePage } from '@/pages/CreateRacePage';
import { RaceDetails } from '@/pages/RaceDetails';
import { AddRunnerPage } from '@/pages/AddRunnerPage';
import { PlaceBetPage } from '@/pages/PlaceBetPage';
import { RaceResultsPage } from '@/pages/RaceResultsPage';
import { LeaderboardPage } from '@/pages/LeaderboardPage';
import { LiveLeaderboardPage } from '@/pages/LiveLeaderboardPage';
import { RecapPage } from '@/pages/RecapPage';
import { EditRacePage } from '@/pages/EditRacePage';
import { Profile } from '@/pages/Profile';
import { EditProfilePage } from '@/pages/EditProfilePage';
import { MyBetsPage } from '@/pages/MyBetsPage';
import { JoinByCodePage } from '@/pages/JoinByCodePage';
import { ScoringPage } from '@/pages/ScoringPage';
import { LegalNoticePage } from '@/pages/LegalNoticePage';
import { TermsPage } from '@/pages/TermsPage';
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuth } from '@/context/AuthContext';

function ProtectedRoute() {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="animate-spin text-emerald-600" size={32} />
            </div>
        );
    }

    if (!isAuthenticated) return <Navigate to="/login" replace />;
    return <Outlet />;
}

function CompleteProfileGuard() {
    const { isProfileComplete } = useAuth();

    if (!isProfileComplete) return <Navigate to="/auth/complete-profile" replace />;
    return <Outlet />;
}

export default function App() {
    return (
        <ErrorBoundary>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/strava/callback" element={<StravaCallback />} />
                <Route path="/auth/google/callback" element={<GoogleCallback />} />
                <Route path="/legal" element={<LegalNoticePage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />

                <Route element={<ProtectedRoute />}>
                    <Route path="/auth/complete-profile" element={<CompleteProfile />} />

                    <Route element={<CompleteProfileGuard />}>
                        <Route element={<MainLayout />}>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/races" element={<RacesPage />} />
                            <Route path="/my-bets" element={<MyBetsPage />} />
                            <Route path="/ranking" element={<RankingPage />} />
                            <Route path="/profile" element={<Profile />} />

                            <Route path="/scoring" element={<ScoringPage />} />
                            <Route path="/profile/edit" element={<EditProfilePage />} />
                            <Route path="/races/create" element={<CreateRacePage />} />
                            <Route path="/races/:id" element={<RaceDetails />} />
                            <Route path="/races/:id/edit" element={<EditRacePage />} />
                            <Route path="/races/:id/add-runner" element={<AddRunnerPage />} />
                            <Route path="/races/:id/bet" element={<PlaceBetPage />} />
                            <Route path="/races/:id/results" element={<RaceResultsPage />} />
                            <Route path="/races/:id/leaderboard" element={<LeaderboardPage />} />
                            <Route path="/races/:id/recap" element={<RecapPage />} />
                            <Route path="/races/:id/live/leaderboard" element={<LiveLeaderboardPage />} />
                            <Route path="/join/:code" element={<JoinByCodePage />} />
                        </Route>
                    </Route>
                </Route>

                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </ErrorBoundary>
    );
}
